<template>
  <div class="domain-section mb-20-px">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <label class="ant-form-item-required domain-label">Domains</label>
        <span class="domain-count ml-2">({{ bulkMode ? bulkValidCount : domains.length }} / {{ max }})</span>
      </div>
      <a-button type="link" size="small" @click="toggleBulkMode">
        <a-icon :type="bulkMode ? 'unordered-list' : 'edit'" />
        {{ bulkMode ? 'Switch to single input' : 'Bulk add' }}
      </a-button>
    </div>

    <template v-if="!bulkMode">
      <div v-for="(domain, index) in domains" :key="index" class="domain-row d-flex align-items-start mb-2">
        <a-input
          v-model="domains[index].url"
          placeholder="https://example.com"
          size="large"
          class="flex-grow-1"
          :class="{ 'has-error-input': errors[index] }"
          @blur="validateDomainAt(index)"
          @input="onUrlInput(index)"
        >
          <a-icon slot="prefix" type="global" />
        </a-input>
        <a-button
          v-if="domains.length > 1"
          type="danger"
          size="large"
          class="ml-2"
          ghost
          @click="removeDomain(index)"
        >
          <a-icon type="delete" />
        </a-button>
      </div>
      <div v-for="(domain, index) in domains" :key="'err-' + index">
        <div v-if="errors[index]" class="ant-form-explain domain-error">{{ errors[index] }}</div>
      </div>
      <a-button
        v-if="domains.length < max"
        type="dashed"
        block
        @click="addDomain"
        class="mt-2"
      >
        <a-icon type="plus" /> Add Domain
      </a-button>
    </template>

    <template v-else>
      <a-textarea
        v-model="bulkText"
        :placeholder="'Paste one URL per line (not separated by commas or spaces):\nhttps://example.com\nhttps://another-site.org\nhttps://third-domain.com'"
        :auto-size="{ minRows: 6, maxRows: 14 }"
      />
      <div class="bulk-status mt-2">
        <span class="bulk-preview-count">{{ bulkValidCount }} valid URL{{ bulkValidCount !== 1 ? 's' : '' }}</span>
        <span v-if="bulkDuplicateCount > 0" class="bulk-warning ml-2">
          ({{ bulkDuplicateCount }} duplicate{{ bulkDuplicateCount !== 1 ? 's' : '' }} removed)
        </span>
        <span v-if="bulkInvalidCount > 0" class="bulk-error-count ml-2">
          {{ bulkInvalidCount }} invalid
        </span>
        <span v-if="bulkValidCount > max" class="bulk-error-count ml-2">
          — exceeds limit of {{ max }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
  import {
    parseBulkUrlInput,
    getUrlValidationError,
    normalizeUrlForDedup,
  } from '../helpers/crawl-validation';

  export default {
    name: 'DomainInput',
    props: {
      value: {
        type: Array,
        default: () => [{ url: '' }],
      },
      max: {
        type: Number,
        default: 30,
      },
    },
    data() {
      return {
        domains: this.value.map((d) => ({ url: d.url || '' })),
        errors: this.value.map(() => ''),
        bulkMode: false,
        bulkText: '',
      };
    },
    computed: {
      bulkParsed() {
        return parseBulkUrlInput(this.bulkText);
      },
      bulkValidCount() {
        return this.bulkParsed.valid.length;
      },
      bulkInvalidCount() {
        return this.bulkParsed.invalid;
      },
      bulkDuplicateCount() {
        return this.bulkParsed.duplicates;
      },
    },
    watch: {
      value(newVal) {
        if (newVal !== this.domains) {
          this.domains = newVal.map((d) => ({ url: d.url || '' }));
          this.errors = newVal.map(() => '');
        }
      },
    },
    methods: {
      emitDomains() {
        this.$emit('input', this.domains);
      },
      onUrlInput(index) {
        this.$set(this.errors, index, '');
        this.emitDomains();
      },
      addDomain() {
        if (this.domains.length < this.max) {
          this.domains.push({ url: '' });
          this.errors.push('');
          this.emitDomains();
        }
      },
      removeDomain(index) {
        this.domains.splice(index, 1);
        this.errors.splice(index, 1);
        this.emitDomains();
      },
      toggleBulkMode() {
        if (this.bulkMode) {
          if (this.bulkParsed.valid.length > 0) {
            this.domains = this.bulkParsed.valid.map((url) => ({ url }));
            this.errors = this.bulkParsed.valid.map(() => '');
            this.emitDomains();
          }
          this.bulkMode = false;
        } else {
          const existing = this.domains.map((d) => d.url.trim()).filter(Boolean);
          this.bulkText = existing.join('\n');
          this.bulkMode = true;
        }
      },
      validateDomainAt(index) {
        this.$set(this.errors, index, getUrlValidationError(this.domains[index].url || ''));
      },
      /**
       * Validates all domains (including duplicate check).
       * If in bulk mode, converts bulk text to domains first.
       * Returns an error string if validation fails, or null if all valid.
       * On success the parent can read the validated domains from the v-model binding.
       */
      validateAll() {
        if (this.bulkMode) {
          const { valid, invalid } = this.bulkParsed;
          if (valid.length === 0) {
            return 'No valid URLs entered.';
          }
          if (invalid > 0) {
            return `${invalid} invalid URL${invalid > 1 ? 's' : ''} found. Please fix them before submitting.`;
          }
          if (valid.length > this.max) {
            return `Too many URLs. Maximum is ${this.max}.`;
          }
          this.domains = valid.map((url) => ({ url }));
          this.errors = valid.map(() => '');
          this.emitDomains();
        }

        let allValid = true;
        const seen = new Set();
        this.domains.forEach((domain, index) => {
          this.validateDomainAt(index);
          if (this.errors[index]) {
            allValid = false;
          } else {
            const normalized = normalizeUrlForDedup(domain.url);
            if (seen.has(normalized)) {
              this.$set(this.errors, index, 'Duplicate domain.');
              allValid = false;
            } else {
              seen.add(normalized);
            }
          }
        });
        return allValid ? null : 'Please fix the errors above.';
      },
      /** Resets to a single empty domain input and exits bulk mode. */
      reset() {
        this.domains = [{ url: '' }];
        this.errors = [''];
        this.bulkMode = false;
        this.bulkText = '';
        this.emitDomains();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .domain-label {
    font-weight: 500;
    margin-bottom: 0;
  }

  .domain-count {
    color: $prLgt;
    font-size: 13px;
  }

  .domain-row {
    .has-error-input {
      border-color: #f5222d;
    }
  }

  .domain-error {
    color: #f5222d;
    font-size: 12px;
    margin-top: -4px;
    margin-bottom: 4px;
  }

  .bulk-status {
    font-size: 13px;
  }

  .bulk-preview-count {
    color: $prLgt;
  }

  .bulk-warning {
    color: #faad14;
  }

  .bulk-error-count {
    color: #f5222d;
  }
</style>
